import { faInfoCircle } from "@fortawesome/pro-solid-svg-icons";
import { LoadingWrapper } from "@vapor/react-contrib-tax-compliance";
import { VaporToolbar } from "@vapor/react-custom";
import Typography from "@vapor/react-extended/ExtendedTypography";
import { VaporIcon } from "@vapor/react-icons";
import {
    Box,
    Button,
    CircularProgress,
    FormControlLabel,
    MenuItem,
    NotificationInline,
    Stack,
    Switch,
    TextField,
} from "@vapor/react-material";
import { DatePicker } from "@vapor/react-x-date-pickers";
import dayjs from "dayjs";
import { useCallback, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import type {
    Intermediary,
    MinimalFulfilment,
} from "../../../../../core/api/use-parse-tax-submission/ParsedTaxSubmissionDTO";
import { PersonType } from "../../../../../core/api/use-parse-tax-submission/ParsedTaxSubmissionDTO";
import { useUploadTaxSubmission } from "../../../../../core/api/use-upload-tax-submission/useUploadTaxSubmission";
import { useSuccessNotification } from "../../../../../core/hooks/useSuccessNotification";
import { getFulfilmentFormValue } from "../../../../utils/uploadForm";

enum TaxSubmissionType {
    F24,
    ANNULLAMENTO,
    OTHERS,
}

export interface FormModel {
    deadline: Date;
    delegatedTaxId: string | null;
    fileName: string;
    fileContent: string;
    intermediaryTaxId: string;
    intermediarySiteCode?: string;
    levy?: { accrualYear?: number; levyId: string };
    governmentAgency?: { id?: string; sendDate?: Date };
}

const NO_DELEGATED = "no_delegated";

function getTaxSubmissionTypeFromFileType(fileType: string) {
    switch (fileType) {
        case "F24A0":
            return TaxSubmissionType.F24;
        case "ANNXX":
            return TaxSubmissionType.ANNULLAMENTO;
        default:
            return TaxSubmissionType.OTHERS;
    }
}

interface TaxSubmissionUploadFormProps {
    delegated: { name: string; taxId: string; recommended: boolean }[];
    fileContent: string;
    fileName: string;
    fileType: string;
    fulfilments: MinimalFulfilment[];
    intermediary: Intermediary;
    isTaxSubmissionUploading: (isUploading: boolean) => unknown;
    onClickCancelButton: () => unknown;
    onUploadTaxSubmission: () => unknown;
    uploadFlow: "ADE" | "SDI" | "TS";
}

export const TaxSubmissionUploadForm: React.FC<
    TaxSubmissionUploadFormProps
> = ({
    delegated,
    fileContent,
    fileName,
    fileType,
    fulfilments,
    intermediary,
    isTaxSubmissionUploading,
    onClickCancelButton,
    onUploadTaxSubmission,
    uploadFlow,
}): JSX.Element => {
    const { t } = useTranslation();
    const { showTextMessage } = useSuccessNotification();

    const sites = useMemo(() => {
        return (
            intermediary.sites?.map((site) => {
                return {
                    siteCode: site.code,
                    siteName: site.name,
                };
            }) || []
        );
    }, [intermediary]);

    const taxSubmissionType = useMemo(
        () => getTaxSubmissionTypeFromFileType(fileType),
        [fileType],
    );

    const defaultDelegatedTaxId = useMemo(() => {
        if (uploadFlow !== "ADE") return null;
        return (
            delegated.find((delegate) => delegate.recommended)?.taxId ||
            NO_DELEGATED
        );
    }, [delegated, uploadFlow]);

    const [formModel, setFormModel] = useState<FormModel>({
        deadline: dayjs().hour(12).toDate(),
        fileContent: fileContent,
        fileName: fileName,
        intermediaryTaxId: intermediary.taxId,
        delegatedTaxId: defaultDelegatedTaxId,
        governmentAgency: undefined,
        intermediarySiteCode: sites[0]?.siteCode || "",
        levy:
            fulfilments.length > 0
                ? {
                      accrualYear: fulfilments[0].accrualYear,
                      levyId: fulfilments[0].levyId,
                  }
                : undefined,
    });

    const isSelectedDelegatedValid = useMemo(() => {
        return (
            (intermediary && intermediary.type === PersonType.PERSON) ||
            !!formModel.delegatedTaxId ||
            uploadFlow !== "ADE"
        );
    }, [formModel.delegatedTaxId, intermediary, uploadFlow]);

    const [
        showGovernmentAgencyDataInputFields,
        setShowGovernmentAgencyDataInputFields,
    ] = useState(false);
    const [
        hasErrorGovernmentAgencyDataInputFields,
        setHasErrorGovernmentAgencyDataInputFields,
    ] = useState(false);

    // use cases
    const {
        isPending: loadingUploadTaxSubmission,
        mutateAsync: uploadTaxSubmission,
    } = useUploadTaxSubmission();
    const onChangeShowGovernmentAgencyDataInputFields = (newValue: boolean) => {
        setShowGovernmentAgencyDataInputFields(newValue);
        if (!newValue) {
            setFormModel((prevValues) => ({
                ...prevValues,
                governmentAgency: undefined,
            }));
        }
    };
    // functions
    const canUploadTaxSubmission = useCallback(() => {
        const isGovernmentAgencyDataValid = showGovernmentAgencyDataInputFields
            ? formModel.governmentAgency?.id &&
              formModel.governmentAgency.id.length > 0 &&
              !hasErrorGovernmentAgencyDataInputFields &&
              formModel.governmentAgency?.sendDate &&
              dayjs(formModel.governmentAgency.sendDate).isValid() &&
              dayjs(formModel.governmentAgency.sendDate).isBefore(
                  dayjs().add(1, "day"),
              )
            : true;

        // flexible deadlines fulfilments require the deadline to be set
        if (
            taxSubmissionType === TaxSubmissionType.OTHERS &&
            formModel.levy &&
            formModel.levy.levyId &&
            !formModel.levy.accrualYear &&
            (!formModel.deadline || !dayjs(formModel.deadline).isValid())
        )
            return false;

        return (
            formModel &&
            formModel.fileContent &&
            (((taxSubmissionType === TaxSubmissionType.ANNULLAMENTO ||
                taxSubmissionType === TaxSubmissionType.F24) &&
                formModel.deadline &&
                dayjs(formModel.deadline).isValid() &&
                !formModel.levy) ||
                taxSubmissionType === TaxSubmissionType.OTHERS) &&
            formModel.fileName &&
            (formModel.intermediarySiteCode || uploadFlow === "SDI") &&
            formModel.intermediaryTaxId &&
            isSelectedDelegatedValid &&
            uploadFlow &&
            isGovernmentAgencyDataValid
        );
    }, [
        formModel,
        hasErrorGovernmentAgencyDataInputFields,
        isSelectedDelegatedValid,
        showGovernmentAgencyDataInputFields,
        taxSubmissionType,
        uploadFlow,
    ]);

    const isFormLoading = loadingUploadTaxSubmission;

    const handleFormChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        if (name === "levy") {
            const [levyId, accrualYear] = value.split("_") as [
                string,
                string | undefined,
            ];

            const selectedLevy = fulfilments.find((levy) => {
                if (!accrualYear) return levy.levyId === levyId;

                return (
                    levy.levyId === levyId &&
                    levy.accrualYear === Number(accrualYear)
                );
            });

            if (selectedLevy) {
                setFormModel((prevValues) => ({
                    ...prevValues,
                    levy: {
                        accrualYear: selectedLevy.accrualYear,
                        levyId: selectedLevy.levyId,
                    },
                }));
            }
        } else if (name === "governmentAgencyId") {
            setFormModel((prevValues) => ({
                ...prevValues,
                governmentAgency: {
                    ...prevValues.governmentAgency,
                    id: value,
                },
            }));

            // Validate governmentAgencyId with regex pattern
            setHasErrorGovernmentAgencyDataInputFields(
                value && !value.match(/^\d{17}$/) ? true : false,
            );
        } else {
            setFormModel((prevValues) => ({
                ...prevValues,
                [name]: value,
            }));
        }
    };

    const handleSubmit = async (isFullUpload: boolean) => {
        isTaxSubmissionUploading(true);

        const deadline =
            (taxSubmissionType === TaxSubmissionType.OTHERS &&
                formModel.levy &&
                !formModel.levy.accrualYear) ||
            taxSubmissionType === TaxSubmissionType.ANNULLAMENTO ||
            taxSubmissionType === TaxSubmissionType.F24
                ? dayjs(formModel.deadline).toISOString()
                : undefined;

        const delegatedTaxId =
            formModel.delegatedTaxId === NO_DELEGATED
                ? null
                : formModel.delegatedTaxId;

        try {
            await uploadTaxSubmission({
                deadline,
                delegatedTaxId,
                fileContent: formModel.fileContent,
                fileName: formModel.fileName,
                intermediarySiteCode: formModel.intermediarySiteCode,
                intermediaryTaxId: formModel.intermediaryTaxId,
                levy: formModel.levy,
                uploadFlow: uploadFlow,
                isFullUpload: isFullUpload,
                ...(formModel.governmentAgency &&
                    formModel.governmentAgency.id &&
                    formModel.governmentAgency.sendDate && {
                        governmentAgency: {
                            id: formModel.governmentAgency.id,
                            sendDate: formModel.governmentAgency.sendDate,
                        },
                    }),
            });
            showTextMessage(
                t(
                    "views.home.components.tax_submission_upload_form.upload_complete",
                ),
            );
            onUploadTaxSubmission();
        } catch {
            // error notification already shown by handleHttpError
        } finally {
            isTaxSubmissionUploading(false);
        }
    };

    const isDeadlineRequired =
        taxSubmissionType === TaxSubmissionType.ANNULLAMENTO ||
        taxSubmissionType === TaxSubmissionType.F24 ||
        (formModel.levy && !formModel.levy.accrualYear);

    return (
        <form>
            <Box>
                <LoadingWrapper
                    spinnerProps={{
                        loadingText: t(
                            "components.loading_wrapper.loading_message",
                        ),
                    }}
                    loading={isFormLoading}
                    variant="spinner"
                >
                    <Stack spacing={1}>
                        <Typography variant="titleMedium">
                            {t(
                                "views.home.components.tax_submission_upload_form.add_detail",
                            )}
                        </Typography>

                        {/* ADEMPIMENTO */}
                        {fulfilments.length > 1 &&
                            taxSubmissionType === TaxSubmissionType.OTHERS && (
                                <TextField
                                    label={t(
                                        "views.home.components.tax_submission_upload_form.fulfilment.label",
                                    )}
                                    name="levy"
                                    onChange={handleFormChange}
                                    select
                                    value={getFulfilmentFormValue(
                                        formModel.levy,
                                    )}
                                >
                                    {fulfilments?.map((fulfilment) => {
                                        const label = `${
                                            fulfilment.name || fulfilment.levyId
                                        } ${fulfilment.accrualYear ? `(${fulfilment.accrualYear})` : ""}`;

                                        return (
                                            <MenuItem
                                                key={`${fulfilment.levyId}_${fulfilment.accrualYear}`}
                                                value={getFulfilmentFormValue({
                                                    levyId: fulfilment.levyId,
                                                    accrualYear:
                                                        fulfilment.accrualYear,
                                                })}
                                            >
                                                {label}
                                            </MenuItem>
                                        );
                                    })}
                                </TextField>
                            )}

                        {(fulfilments.length === 1 ||
                            taxSubmissionType ===
                                TaxSubmissionType.ANNULLAMENTO ||
                            taxSubmissionType === TaxSubmissionType.F24) && (
                            <TextField
                                label={t(
                                    "views.home.components.tax_submission_upload_form.fulfilment.label",
                                )}
                                readOnly={true}
                                value={
                                    taxSubmissionType === TaxSubmissionType.F24
                                        ? t(
                                              "views.home.components.tax_submission_upload_form.fulfilment.f24",
                                          )
                                        : taxSubmissionType ===
                                            TaxSubmissionType.ANNULLAMENTO
                                          ? t(
                                                "views.home.components.tax_submission_upload_form.fulfilment.annullamento",
                                            )
                                          : `${
                                                fulfilments[0].name ||
                                                fulfilments[0].levyId
                                            } ${fulfilments[0].accrualYear ? `(${fulfilments[0].accrualYear})` : ""}`
                                }
                            />
                        )}

                        {/* Deadline */}
                        {isDeadlineRequired && (
                            <DatePicker
                                label={t(
                                    "views.home.components.tax_submission_upload_form.deadline",
                                )}
                                onChange={(newValue) => {
                                    setFormModel((prevValues) => ({
                                        ...prevValues,
                                        deadline: dayjs(newValue as Date)
                                            .hour(12)
                                            .toDate(),
                                    }));
                                }}
                                readOnly={
                                    isFormLoading ||
                                    taxSubmissionType ===
                                        TaxSubmissionType.ANNULLAMENTO
                                }
                                sx={{ mb: 3 }}
                                value={formModel.deadline}
                            />
                        )}

                        {/* Intermediary */}
                        <TextField
                            label={t(
                                "views.home.components.tax_submission_upload_form.intermediary",
                            )}
                            name="intermediaryTaxId"
                            readOnly
                            value={intermediary.name || intermediary.taxId}
                        />

                        {/* Site */}
                        {sites.length > 0 && (
                            <TextField
                                label={t(
                                    "views.home.components.tax_submission_upload_form.site",
                                )}
                                name="intermediarySiteCode"
                                onChange={handleFormChange}
                                readOnly={sites.length === 1 || isFormLoading}
                                select
                                value={formModel.intermediarySiteCode}
                            >
                                {sites.map((site) => (
                                    <MenuItem
                                        key={site.siteCode}
                                        value={site.siteCode}
                                    >
                                        {site.siteName}
                                    </MenuItem>
                                ))}
                            </TextField>
                        )}
                        {sites.length === 0 && (
                            <TextField
                                error={
                                    uploadFlow === "ADE" || uploadFlow === "TS"
                                }
                                helperText={
                                    uploadFlow === "ADE" || uploadFlow === "TS"
                                        ? t(
                                              "views.home.components.tax_submission_upload_form.errors.no_site",
                                          )
                                        : undefined
                                }
                                label={t(
                                    "views.home.components.tax_submission_upload_form.site",
                                )}
                                readOnly={true}
                                value={t(
                                    "views.home.components.tax_submission_upload_form.no_site",
                                )}
                            />
                        )}

                        {/* Delegated */}
                        {uploadFlow === "ADE" && delegated.length > 0 && (
                            <TextField
                                label={t(
                                    "views.home.components.tax_submission_upload_form.delegated",
                                )}
                                name="delegatedTaxId"
                                onChange={handleFormChange}
                                readOnly={isFormLoading}
                                select
                                SelectProps={{ displayEmpty: true }}
                                value={formModel.delegatedTaxId}
                            >
                                <MenuItem value={NO_DELEGATED}>
                                    {t(
                                        "views.home.components.tax_submission_upload_form.no_delegated",
                                    )}
                                </MenuItem>
                                {[
                                    ...delegated.filter(
                                        (item) => item.recommended,
                                    ),
                                    ...delegated.filter(
                                        (item) => !item.recommended,
                                    ),
                                ]?.map((delegated) => (
                                    <MenuItem
                                        key={delegated.taxId}
                                        value={delegated.taxId}
                                    >
                                        {delegated.name || delegated.taxId}
                                    </MenuItem>
                                ))}
                            </TextField>
                        )}

                        {/* Government Agency Data Section */}
                        {uploadFlow === "ADE" && (
                            <>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={
                                                showGovernmentAgencyDataInputFields
                                            }
                                            onChange={(e) =>
                                                onChangeShowGovernmentAgencyDataInputFields(
                                                    e.target.checked,
                                                )
                                            }
                                            disabled={isFormLoading}
                                            sx={{ ml: -1 }}
                                        />
                                    }
                                    label={t(
                                        "views.home.components.tax_submission_upload_form.government_agency_data_toggle",
                                    )}
                                />

                                {showGovernmentAgencyDataInputFields && (
                                    <>
                                        <TextField
                                            label={t(
                                                "views.home.components.tax_submission_upload_form.government_agency_data.id",
                                            )}
                                            name="governmentAgencyId"
                                            onChange={handleFormChange}
                                            readOnly={isFormLoading}
                                            value={
                                                formModel.governmentAgency
                                                    ?.id || ""
                                            }
                                            error={
                                                hasErrorGovernmentAgencyDataInputFields
                                            }
                                            helperText={
                                                hasErrorGovernmentAgencyDataInputFields
                                                    ? t(
                                                          "views.home.components.tax_submission_upload_form.errors.government_agency_data.id.not_valid",
                                                      )
                                                    : ""
                                            }
                                        />
                                        <Typography variant="bodySmall">
                                            {t(
                                                "views.home.components.tax_submission_upload_form.government_agency_data.id.subtitle",
                                            )}
                                        </Typography>
                                        <DatePicker
                                            label={t(
                                                "views.home.components.tax_submission_upload_form.government_agency_data.send_date",
                                            )}
                                            name="governmentAgencySendDate"
                                            onChange={(newValue) => {
                                                setFormModel((prevValues) => ({
                                                    ...prevValues,
                                                    governmentAgency: {
                                                        ...prevValues.governmentAgency,
                                                        sendDate: dayjs(
                                                            newValue as Date,
                                                        )
                                                            .hour(12)
                                                            .toDate(),
                                                    },
                                                }));
                                            }}
                                            readOnly={isFormLoading}
                                            value={
                                                formModel.governmentAgency
                                                    ?.sendDate
                                            }
                                            maxDate={dayjs().toDate()}
                                        />
                                        <NotificationInline
                                            icon={
                                                <VaporIcon
                                                    color="info"
                                                    icon={faInfoCircle}
                                                    size="24px"
                                                />
                                            }
                                            severity="info"
                                            variant="outlined"
                                        >
                                            {t(
                                                "views.home.components.tax_submission_upload_form.government_agency_data.alert",
                                            )}
                                        </NotificationInline>
                                        <NotificationInline
                                            icon={
                                                <VaporIcon
                                                    color="info"
                                                    icon={faInfoCircle}
                                                    size="24px"
                                                />
                                            }
                                            severity="info"
                                            variant="outlined"
                                        >
                                            <Trans
                                                i18nKey={t(
                                                    "views.home.components.tax_submission_upload_form.government_agency_data.credentials.alert",
                                                )}
                                                components={{
                                                    bold: (
                                                        <Typography
                                                            variant="body"
                                                            component="span"
                                                            fontWeight={500}
                                                        />
                                                    ),
                                                }}
                                            />
                                        </NotificationInline>
                                    </>
                                )}
                            </>
                        )}
                    </Stack>
                </LoadingWrapper>
            </Box>

            <Box
                sx={{
                    ml: -4,
                    //  "div:nth-of-type(2)": { marginRight: 1 }
                }}
            >
                <VaporToolbar
                    contentLeft={
                        <Button
                            disabled={loadingUploadTaxSubmission}
                            onClick={onClickCancelButton}
                        >
                            {t(
                                "views.home.components.tax_submission_upload_form.actions.cancel",
                            )}
                        </Button>
                    }
                    contentRight={[
                        <Button
                            key="upload"
                            disabled={
                                !canUploadTaxSubmission() ||
                                loadingUploadTaxSubmission
                            }
                            onClick={(e) => {
                                e.preventDefault();
                                handleSubmit(false);
                            }}
                            variant={
                                showGovernmentAgencyDataInputFields
                                    ? "contained"
                                    : "outlined"
                            }
                        >
                            {t(
                                "views.home.components.tax_submission_upload_form.actions.upload",
                            )}
                            {loadingUploadTaxSubmission && (
                                // loadingUploadTaxSubmission.mode === TODO
                                //     "UPLOAD" &&
                                <CircularProgress
                                    color="inherit"
                                    sx={{ ml: 1 }}
                                    size={16}
                                />
                            )}
                        </Button>,
                        ...(showGovernmentAgencyDataInputFields
                            ? []
                            : [
                                  <Button
                                      key="uploadAndSend"
                                      disabled={
                                          !canUploadTaxSubmission() ||
                                          (intermediary.type ===
                                              PersonType.COMPANY &&
                                              formModel.delegatedTaxId ===
                                                  NO_DELEGATED) ||
                                          loadingUploadTaxSubmission
                                      }
                                      onClick={(e) => {
                                          e.preventDefault();
                                          handleSubmit(true);
                                      }}
                                      variant="contained"
                                  >
                                      {t(
                                          "views.home.components.tax_submission_upload_form.actions.upload_and_send",
                                      )}
                                      {loadingUploadTaxSubmission && (
                                          //   loadingUploadTaxSubmission.mode ===
                                          //       "UPLOAD_AND_SEND" && TODO
                                          <CircularProgress
                                              color="inherit"
                                              sx={{ ml: 1 }}
                                              size={16}
                                          />
                                      )}
                                  </Button>,
                              ]),
                    ]}
                    size="medium"
                    variant="regular"
                />
            </Box>
        </form>
    );
};
